import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Box,
    Chip,
    CircularProgress,
    Alert,
    IconButton,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * This new helper function is more robust. It dynamically creates a descriptive
 * label from the time range data itself, rather than relying on a static map.
 * This ensures the times will always show correctly.
 * @param {object} range - The time range object, e.g., { start: '06:00', end: '09:00' }
 * @returns {string} - A descriptive label, e.g., "Morning (6 AM - 9 AM)"
 */
const getTimeLabel = (range) => {
    const startHour = parseInt(range.start.split(':')[0], 10);
    let period = 'Time';

    if (startHour >= 6 && startHour <= 9) period = 'Morning';
    else if (startHour >= 10 && startHour <= 12) period = 'Midday';
    else if (startHour >= 13 && startHour <= 16) period = 'Afternoon';
    else if (startHour >= 17 && startHour <= 20) period = 'Evening';
    else if (startHour >= 21) period = 'Night';

    // Function to convert 24-hour time to 12-hour AM/PM format
    const formatTo12Hour = (time) => {
        const [hour, minute] = time.split(':');
        const h = parseInt(hour, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const formattedHour = h % 12 === 0 ? 12 : h % 12;
        return `${formattedHour}${minute !== '00' ? `:${minute}` : ''} ${ampm}`;
    };

    return `${period} (${formatTo12Hour(range.start)} - ${formatTo12Hour(range.end)})`;
};

const PreferencesModal = ({ open, onClose, loading, error, preferences }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: '#202020',
                    color: '#fff',
                    borderRadius: 2,
                    border: '1px solid #333',
                    boxShadow: 5,
                }
            }}
        >
            <DialogTitle sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {preferences ? `${preferences.name}'s Preferences` : 'Loading Preferences...'}
                </Typography>
                <IconButton onClick={onClose} sx={{ color: '#fff' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 2 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                        <CircularProgress sx={{ color: '#12c099' }} />
                    </Box>
                )}
                {error && (
                    <Alert
                        severity="info"
                        sx={{ my: 2, backgroundColor: '#333', color: '#ccc' }}
                    >
                        {error}
                    </Alert>
                )}
                {preferences && !loading && !error && (
                    <Box>
                        <Box mb={3}>
                            <Typography variant="overline" sx={{ color: '#ccc' }}>Available Days</Typography>
                            <Divider sx={{ mb: 1.5, borderColor: '#444' }} />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {preferences.preferredDays.length > 0 ? (
                                    preferences.preferredDays.map(day => (
                                        <Chip
                                            key={day}
                                            label={day}
                                            sx={{ bgcolor: '#12c099', color: '#000', fontWeight: 500 }}
                                        />
                                    ))
                                ) : (
                                    <Typography variant="body2" sx={{ color: '#888' }}>Not specified</Typography>
                                )}
                            </Box>
                        </Box>

                        <Box mb={3}>
                            <Typography variant="overline" sx={{ color: '#ccc' }}>Available Times</Typography>
                            <Divider sx={{ mb: 1.5, borderColor: '#444' }} />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {preferences.timeRanges.length > 0 ? (
                                    preferences.timeRanges.map(range => (
                                        <Chip
                                            key={range.start}
                                            // ✅ THIS IS THE FIX: Use the new dynamic helper function
                                            label={getTimeLabel(range)}
                                            variant="outlined"
                                            sx={{ color: '#ccc', borderColor: '#555' }}
                                        />
                                    ))
                                ) : (
                                    <Typography variant="body2" sx={{ color: '#888' }}>Not specified</Typography>
                                )}
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="overline" sx={{ color: '#ccc' }}>Notes</Typography>
                            <Divider sx={{ mb: 1.5, borderColor: '#444' }} />
                            <Typography variant="body2" sx={{ color: '#ccc', fontStyle: 'italic' }}>
                                {preferences.notes || 'No notes provided.'}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PreferencesModal;